import {
  createRouter,
  createRoute,
  createRootRouteWithContext,
  redirect,
  lazyRouteComponent,
} from '@tanstack/react-router';
import AppLayout from '@/layouts/AppLayout';
import StudyLayout from '@/layouts/StudyLayout';

// ─── Router context type ────────────────────────────────────────────────────

export interface RouterContext {
  isAuthenticated: boolean;
}

// ─── Root (protected by auth check) ─────────────────────────────────────────

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: AppLayout,
  beforeLoad: ({ context, location }) => {
    // Allow access to /login without authentication
    if (location.pathname === '/login') return;

    if (!context.isAuthenticated) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
});

// ─── Login route (public) ──────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ context }) => {
    // Already authenticated? Don't show the login page — go to the dashboard.
    if (context.isAuthenticated) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: lazyRouteComponent(() => import('@/pages/LoginPage')),
});

// ─── Index redirect ─────────────────────────────────────────────────────────

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

// ─── Dashboard ──────────────────────────────────────────────────────────────

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: lazyRouteComponent(() => import('@/pages/DashboardPage')),
});

// ─── Studies list ───────────────────────────────────────────────────────────

const studiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/studies',
  component: lazyRouteComponent(() => import('@/pages/StudyListPage')),
});

// ─── Study workspace (layout with tabs) ─────────────────────────────────────

const studyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/studies/$studyId',
  component: StudyLayout,
});

const studyIndexRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/studies/$studyId/overview', params });
  },
});

const studyOverviewRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/overview',
  component: lazyRouteComponent(() => import('@/pages/study/StudyWorkspacePage')),
});

const milestoneBoardRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/milestones',
  component: lazyRouteComponent(() => import('@/pages/study/MilestoneBoardPage')),
});

const countriesRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/countries',
  component: lazyRouteComponent(() => import('@/pages/study/CountriesPage')),
});

const sitesRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/sites',
  component: lazyRouteComponent(() => import('@/pages/study/SiteListPage')),
});

const siteDetailRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/sites/$siteId',
  component: lazyRouteComponent(() => import('@/pages/study/SiteDetailPage')),
});

const feasibilityRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/feasibility',
  component: lazyRouteComponent(() => import('@/pages/study/FeasibilityPage')),
});

const activationRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/activation',
  component: lazyRouteComponent(() => import('@/pages/study/ActivationPage')),
});

const enrollmentRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/enrollment',
  component: lazyRouteComponent(() => import('@/pages/study/EnrollmentDashboardPage')),
});

const monitoringRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/monitoring',
  component: lazyRouteComponent(() => import('@/pages/study/MonitoringCalendarPage')),
});

const visitDetailRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/monitoring/$visitId',
  component: lazyRouteComponent(() => import('@/pages/study/MonitoringVisitDetailPage')),
});

const tripReportEditorRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/monitoring/$visitId/trip-report',
  component: lazyRouteComponent(() => import('@/pages/study/TripReportEditorPage')),
});

const actionItemsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/action-items',
  component: lazyRouteComponent(() => import('@/pages/study/ActionItemsPage')),
});

const issuesRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/issues',
  component: lazyRouteComponent(() => import('@/pages/study/IssuesPage')),
});

const financialsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/financials',
  component: lazyRouteComponent(() => import('@/pages/study/FinancialsPage')),
});

const vendorsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/vendors',
  component: lazyRouteComponent(() => import('@/pages/study/VendorsPage')),
});

const documentsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/documents',
  component: lazyRouteComponent(() => import('@/pages/study/DocumentsPage')),
});

const reportingRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/reporting',
  component: lazyRouteComponent(() => import('@/pages/study/ReportingPage')),
});

const auditReadinessRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/audit-readiness',
  component: lazyRouteComponent(() => import('@/pages/study/AuditReadinessPage')),
});

const rbqmRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/rbqm',
  component: lazyRouteComponent(() => import('@/pages/study/RbqmPage')),
});

const signaturesRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/signatures',
  // Optional prefill so other pages (e.g. Trip Report editor) can deep-link a
  // specific high-value artifact straight into the sign-off form.
  validateSearch: (search: Record<string, unknown>) => ({
    entityType:
      typeof search.entityType === 'string' ? search.entityType : undefined,
    entityId: typeof search.entityId === 'string' ? search.entityId : undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/study/SignaturesPage')),
});

const safetyRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/safety',
  component: lazyRouteComponent(() => import('@/pages/study/SafetyPage')),
});

const paymentsExportRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/payments-export',
  component: lazyRouteComponent(() => import('@/pages/study/PaymentsExportPage')),
});

const reportBuilderRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/report-builder',
  component: lazyRouteComponent(() => import('@/pages/study/ReportBuilderPage')),
});

const workflowConfigRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/workflow-config',
  component: lazyRouteComponent(() => import('@/pages/study/WorkflowConfigPage')),
});

const subjectsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/subjects',
  component: lazyRouteComponent(() => import('@/pages/study/SubjectsPage')),
});

const resourcesRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/resources',
  component: lazyRouteComponent(() => import('@/pages/study/ResourcesPage')),
});

const supplyRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/supply',
  component: lazyRouteComponent(() => import('@/pages/study/SupplyPage')),
});

const closeoutRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/closeout',
  component: lazyRouteComponent(() => import('@/pages/study/CloseoutPage')),
});

const insightsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/insights',
  component: lazyRouteComponent(() => import('@/pages/study/InsightsPage')),
});

const studySetupRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/setup',
  component: lazyRouteComponent(() => import('@/pages/study/StudySetupPage')),
});

const studyStartupRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/startup',
  component: lazyRouteComponent(() => import('@/pages/study/StudyStartupPage')),
});

const protocolVersionsRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/protocol-versions',
  component: lazyRouteComponent(() => import('@/pages/study/ProtocolVersionsPage')),
});

const regulatoryRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: '/regulatory',
  component: lazyRouteComponent(() => import('@/pages/study/RegulatorySubmissionsPage')),
});

// ─── Global pages ───────────────────────────────────────────────────────────

const investigatorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/investigators',
  component: lazyRouteComponent(() => import('@/pages/InvestigatorsPage')),
});

const auditLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit-log',
  component: lazyRouteComponent(() => import('@/pages/AuditLogPage')),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('@/pages/SettingsPage')),
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('@/pages/NotificationsPage')),
});

const aiGovernanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ai-governance',
  component: lazyRouteComponent(() => import('@/pages/AiGovernancePage')),
});

const myWorkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-work',
  component: lazyRouteComponent(() => import('@/pages/MyWorkPage')),
});

// ─── Route tree ─────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  dashboardRoute,
  studiesRoute,
  studyRoute.addChildren([
    studyIndexRoute,
    studyOverviewRoute,
    milestoneBoardRoute,
    countriesRoute,
    sitesRoute,
    siteDetailRoute,
    feasibilityRoute,
    activationRoute,
    enrollmentRoute,
    monitoringRoute,
    visitDetailRoute,
    tripReportEditorRoute,
    actionItemsRoute,
    issuesRoute,
    financialsRoute,
    vendorsRoute,
    documentsRoute,
    reportingRoute,
    auditReadinessRoute,
    rbqmRoute,
    signaturesRoute,
    safetyRoute,
    paymentsExportRoute,
    reportBuilderRoute,
    workflowConfigRoute,
    subjectsRoute,
    resourcesRoute,
    supplyRoute,
    closeoutRoute,
    insightsRoute,
    studySetupRoute,
    studyStartupRoute,
    protocolVersionsRoute,
    regulatoryRoute,
  ]),
  investigatorsRoute,
  auditLogRoute,
  settingsRoute,
  notificationsRoute,
  aiGovernanceRoute,
  myWorkRoute,
]);

// ─── Router instance ────────────────────────────────────────────────────────

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    isAuthenticated: false, // default, overridden at render time
  },
});

// ─── Type registration ──────────────────────────────────────────────────────

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
